import React, { useEffect, useState } from "react";
import { Save, Loader2, Plus, Trash2, Rocket } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { toast } from "sonner";
import { api, errText } from "@/lib/devadminApi";

export function SoftLaunch({ eventId }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/events/${eventId}/soft-launch`).then((r) => setCfg(r.data))
    .catch((e) => toast.error(errText(e)));
  useEffect(() => { setCfg(null); load(); }, [eventId]); // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/events/${eventId}/soft-launch`, {
        enabled: cfg.enabled,
        allowed_phones: (cfg.allowed_phones || []).map((p) => p.trim()).filter(Boolean),
        message: cfg.message,
      });
      setCfg({ ...cfg, ...data });
      toast.success(data.enabled ? "Soft launch is ON — only invited numbers can register" : "Soft launch is OFF — registration is open to everyone");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  if (!cfg) return <div className="mono-label" data-testid="soft-launch-loading">Loading soft launch…</div>;

  const phones = cfg.allowed_phones || [];
  const setPhones = (v) => setCfg({ ...cfg, allowed_phones: v });

  return (
    <Panel className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <MonoLabel>Soft launch · {cfg.registrations ?? 0} pilot registrations so far</MonoLabel>
        <button data-testid="sl-toggle" onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })}
          className={cfg.enabled ? "btn-red" : "btn-ghost"}>
          <Rocket size={14} /> {cfg.enabled ? "Invite-only: ON" : "Invite-only: OFF"}
        </button>
      </div>

      <label className="field-label mt-6 block">
        <span>Message shown to everyone not on the list</span>
        <textarea data-testid="sl-message" rows={3} className="input-x" value={cfg.message || ""}
          onChange={(e) => setCfg({ ...cfg, message: e.target.value })} />
      </label>

      <div className="mt-6 flex items-center justify-between">
        <MonoLabel>Invited numbers · {phones.length}</MonoLabel>
        <button data-testid="sl-add-phone" className="btn-ghost" onClick={() => setPhones([...phones, ""])}>
          <Plus size={14} /> Add number
        </button>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {phones.length === 0 && (
          <div className="mono-label" style={{ color: "var(--taupe)" }}>No invites yet — add the numbers for your pilot group</div>
        )}
        {phones.map((p, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`sl-phone-row-${i}`}>
            <input data-testid={`sl-phone-${i}`} className="input-x !py-2" placeholder="10-digit mobile" value={p}
              onChange={(e) => setPhones(phones.map((x, idx) => idx === i ? e.target.value : x))} />
            <button data-testid={`sl-phone-del-${i}`} className="p-2 hover:text-[var(--red)]"
              onClick={() => setPhones(phones.filter((_, idx) => idx !== i))}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      <button data-testid="sl-save" className="btn-red mt-6" disabled={busy} onClick={save}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save soft launch
      </button>

      <div className="mono-label mt-4" style={{ color: "var(--muted)" }}>
        While invite-only is ON, the public page shows "Registration opens soon"; invited numbers unlock the form by
        entering their WhatsApp number. Pilot registrations are tagged and tracked in Admin → Soft Launch.
      </div>
    </Panel>
  );
}
